import React, { useEffect, useState } from "react"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { useAuth } from "services/auth/auth-service-hooks.ts"
import { OreveButton } from "components/button/OreveButton.tsx"
import { DialogProps, Typography } from "@mui/material"
import { useUserContext } from "context/UserContext.tsx"
import { UnauthorizedAccessPopup } from "pages/PopupWarningRight.tsx"
import { LoaderPage } from "pages/LoaderPage.tsx"
import qr_code_download from "assets/svg/qr_code_download.svg"
import { Box } from "@mui/system"
import {
    MessageCustomerMigration,
    useFeatureFlags,
} from "context/FeatureFlagsContext.tsx"

// Narrow type for API response { value: boolean }
const isToggleResponse = (x: unknown): x is { value: boolean } =>
    typeof x === "object" &&
    x !== null &&
    "value" in x &&
    typeof (x as Record<"value", unknown>).value === "boolean"

interface MiddlewareProps {
    children: React.ReactNode
}

const Middleware = ({ children }: MiddlewareProps) => {
    const { profileService, organizationService, supportService } =
        useMmsService()
    const { usePostSendEmail } = profileService
    const { logout } = useAuth()
    const rawUser = useUserContext()
    const [openWrongUserRight, setOpenWrongUserRight] = useState<
        boolean | null
    >(null)
    const [openEmailIsNotVerified, setOpenEmailIsNotVerified] = useState<
        boolean | null
    >(null)

    const { sendRequest: resetPassword, status: sendEmailStatus } =
        usePostSendEmail(rawUser.email)
    const {
        data: multiOrganizationData,
        sendRequest: requestMultiOrganizationIsEnabled,
    } = organizationService.useGetEnableMultiOrganization()

    const {
        data: supportAutochargeEnabledData,
        sendRequest: requestVidAutochargeEnabled,
    } = supportService.useGetEnableVidAutocharge()

    const {
        data: gdprMessageEnabledData,
        sendRequest: requestEnableGdprMessage,
    } = supportService.useEnableGdprMessage()

    // Global feature flags
    const {
        setMultiOrganizationEnabled,
        setVidAutochargeEnabled,
        setGdprMessageEnabled,
    } = useFeatureFlags()
    const handleClose: DialogProps["onClose"] = (
        _event: React.SyntheticEvent,
        reason
    ) => {
        if (reason && reason === "backdropClick") return
        setOpenWrongUserRight(false)
        setOpenEmailIsNotVerified(false)
    }

    useEffect(() => {
        // API returns { value: true }
        if (isToggleResponse(multiOrganizationData)) {
            setMultiOrganizationEnabled(multiOrganizationData.value)
        }
        if (isToggleResponse(supportAutochargeEnabledData)) {
            setVidAutochargeEnabled(supportAutochargeEnabledData.value)
        }
        setGdprMessageEnabled(
            gdprMessageEnabledData as MessageCustomerMigration
        )
    }, [
        multiOrganizationData,
        supportAutochargeEnabledData,
        gdprMessageEnabledData,
    ])

    useEffect(() => {
        if (sendEmailStatus === 200) {
            logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            })
        }
    }, [sendEmailStatus])

    useEffect(() => {
        if (
            rawUser?.isAuthenticated &&
            !rawUser?.isLoading &&
            rawUser.is_email_verified !== undefined &&
            rawUser.is_driver !== undefined &&
            rawUser.is_manager !== undefined
        ) {
            if (openEmailIsNotVerified == null && openWrongUserRight == null) {
                if (!rawUser.is_manager) setOpenWrongUserRight(true)
                else if (!rawUser.is_email_verified)
                    setOpenEmailIsNotVerified(true)
                else if (rawUser.emsp_partner_name == null)
                    setOpenWrongUserRight(true)
                else {
                    setOpenWrongUserRight(false)
                    setOpenEmailIsNotVerified(false)
                }
            }
            requestMultiOrganizationIsEnabled()
            requestVidAutochargeEnabled()
            requestEnableGdprMessage()
        }
    }, [rawUser])

    if (openEmailIsNotVerified == null && openWrongUserRight == null) {
        return <LoaderPage />
    } else {
        if (openWrongUserRight) {
            return (
                <UnauthorizedAccessPopup
                    open={openWrongUserRight}
                    onClose={handleClose}
                    iconTitle={openWrongUserRight}
                    popinTitle="Désolé vous n'avez pas les droits d'accès à cette plateforme."
                >
                    <Box sx={{ marginTop: "0rem" }}>
                        <Typography sx={{ marginY: "1rem" }}>
                            Cet espace est dédié aux professionnels. Contactez
                            notre service commercial pour devenir partenaire
                            oreve : support@obornes.fr.
                        </Typography>
                        <Typography sx={{ marginY: "1rem" }}>
                            Si vous êtes un particulier vous pouvez télécharger
                            notre application mobile OREVE pour vous recharger
                            sur notre réseau.
                        </Typography>
                        <Typography sx={{ marginY: "1rem" }}>
                            Scanner le code QR ci-dessous :
                        </Typography>
                        <img src={qr_code_download} alt="qr_code_download" />,
                    </Box>
                    <OreveButton
                        sx={{ marginY: "0.5rem" }}
                        variant="outlined"
                        color="primary"
                        value="Déconnexion"
                        onClick={() => {
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin,
                                },
                            })
                        }}
                    />
                </UnauthorizedAccessPopup>
            )
        } else if (openEmailIsNotVerified) {
            return (
                <UnauthorizedAccessPopup
                    open={openEmailIsNotVerified}
                    onClose={handleClose}
                    iconTitle={false}
                    popinTitle="Désolé vous n'avez pas accès à cet espace car vous n'avez pas encore validé votre e-mail"
                >
                    <Typography sx={{ marginY: "1rem" }}>
                        Nous allons vous renvoyer un e-mail de validation et
                        vous redirecter vers la page de connexion.
                    </Typography>
                    <OreveButton
                        sx={{ marginY: "1rem" }}
                        variant="outlined"
                        color="primary"
                        value="Renvoyer l'e-mail de validation"
                        onClick={() => resetPassword()}
                    />
                </UnauthorizedAccessPopup>
            )
        } else {
            return <>{children}</>
        }
    }
}

export default Middleware
